// Utility functions for Import from Swagger feature

import { analyzeUrlForEnvironment, type EnvironmentInfo } from '../../utils/environmentUtils';

export type SwaggerPreviewEnvironment = {
  name?: string;
  baseUrl?: string;
  environmentType?: string;
  description?: string;
  isNew?: boolean;
};

function isLoopbackHost(hostname: string): boolean {
  const host = hostname.trim().toLowerCase();
  return (
    host === 'localhost' ||
    host === '127.0.0.1' ||
    host === '0.0.0.0' ||
    host === '::1' ||
    host.endsWith('.localhost')
  );
}

function isLoopbackBaseUrl(baseUrl: string): boolean {
  try {
    const parsed = new URL(baseUrl.includes('://') ? baseUrl.trim() : `http://${baseUrl.trim()}`);
    return isLoopbackHost(parsed.hostname);
  } catch {
    return /localhost|127\.0\.0\.1/i.test(baseUrl);
  }
}

function originFromRemoteSwaggerUrl(swaggerUrl?: string): string | null {
  const trimmed = swaggerUrl?.trim();
  if (!trimmed) return null;
  try {
    const parsed = new URL(trimmed);
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return null;
    if (!parsed.hostname || isLoopbackHost(parsed.hostname)) return null;
    return `${parsed.protocol}//${parsed.hostname}${parsed.port ? `:${parsed.port}` : ''}`;
  } catch {
    return null;
  }
}

/**
 * Build import payload for environment association.
 * When a matching environment already exists, the backend reuses it — do not disable association.
 */
export function buildSwaggerEnvironmentImportOptions(params: {
  createEnvironment: boolean;
  previewEnvironment?: SwaggerPreviewEnvironment | null;
  swaggerUrl?: string;
  isFileImport?: boolean;
}): { createEnvironment: boolean; environmentInfo?: EnvironmentInfo } {
  if (params.createEnvironment !== true) {
    return { createEnvironment: false };
  }

  const previewEnv = params.previewEnvironment;
  if (previewEnv?.baseUrl) {
    const remoteOrigin = originFromRemoteSwaggerUrl(params.swaggerUrl);
    if (remoteOrigin && isLoopbackBaseUrl(previewEnv.baseUrl) && !params.isFileImport) {
      try {
        return {
          createEnvironment: true,
          environmentInfo: analyzeUrlForEnvironment(params.swaggerUrl!.trim()),
        };
      } catch {
        return {
          createEnvironment: true,
          environmentInfo: {
            name: previewEnv.name ?? 'Imported Environment',
            baseUrl: remoteOrigin,
            environmentType: previewEnv.environmentType ?? 'Production',
            description: previewEnv.description ?? '',
          },
        };
      }
    }
    return {
      createEnvironment: true,
      environmentInfo: {
        name: previewEnv.name ?? 'Imported Environment',
        baseUrl: previewEnv.baseUrl,
        environmentType: previewEnv.environmentType ?? 'Development',
        description: previewEnv.description ?? '',
      },
    };
  }

  if (!params.isFileImport && params.swaggerUrl?.trim()) {
    try {
      return {
        createEnvironment: true,
        environmentInfo: analyzeUrlForEnvironment(params.swaggerUrl),
      };
    } catch {
      return { createEnvironment: true };
    }
  }

  return { createEnvironment: true };
}

// Helper to resolve $ref pointers in OpenAPI spec
function resolveRef(ref: string, spec: any) {
  if (!ref.startsWith('#/')) return null;
  const parts = ref.slice(2).split('/');
  let result = spec;
  for (const part of parts) {
    result = result[part];
    if (!result) return null;
  }
  return result;
}

// Helper to detect content type from request body
function detectContentType(op: any): string {
  if (op.requestBody && op.requestBody.content) {
    const content = op.requestBody.content as any;
    const contentTypes = Object.keys(content);
    
    // Check for specific content types
    if (contentTypes.includes('application/json')) return 'JSON';
    if (contentTypes.includes('text/plain')) return 'Text';
    if (contentTypes.includes('application/javascript')) return 'JavaScript';
    if (contentTypes.includes('text/html')) return 'HTML';
    if (contentTypes.includes('application/xml') || contentTypes.includes('text/xml')) return 'XML';
    
    // Default to JSON if no specific type found
    return 'JSON';
  }
  return 'JSON';
}

// Server-set fields to exclude from request body sample unless they are in schema.required
const EXCLUDED_SERVER_SET_FIELDS = new Set([
  'id', 'createdat', 'updatedat', 'createdby', 'updatedby',
  'created_at', 'updated_at', 'created_by', 'updated_by'
]);

function isExcludedServerSetField(key: string): boolean {
  const lower = key.toLowerCase();
  const normalized = lower.replace(/_/g, '');
  return EXCLUDED_SERVER_SET_FIELDS.has(normalized) || EXCLUDED_SERVER_SET_FIELDS.has(lower);
}

// Include all properties (mandatory + optional) except common server-set fields (id, updatedAt, createdBy, etc.).
function shouldIncludeProperty(key: string): boolean {
  return !isExcludedServerSetField(key);
}

// Recursively strip server-set fields from object/array so they never appear in the final body.
function stripExcludedServerSetFields(obj: any): any {
  if (obj == null) return obj;
  if (Array.isArray(obj)) {
    return obj.map(item => stripExcludedServerSetFields(item));
  }
  if (typeof obj === 'object') {
    const out: any = {};
    for (const [key, value] of Object.entries(obj)) {
      if (isExcludedServerSetField(key)) continue;
      out[key] = stripExcludedServerSetFields(value);
    }
    return out;
  }
  return obj;
}

// Helper to generate sample body from schema (mandatory + optional, excluding server-set fields).
function generateSampleBody(schema: any): string {
  if (!schema) return '';
  
  try {
    if (schema.type === 'object' && schema.properties) {
      const sample: any = {};
      Object.entries(schema.properties).forEach(([key, prop]: [string, any]) => {
        if (!shouldIncludeProperty(key)) return;
        const propType = prop && prop.type;
        if (propType === 'string') {
          sample[key] = 'string';
        } else if (propType === 'number') {
          sample[key] = 0;
        } else if (propType === 'integer') {
          sample[key] = 0;
        } else if (propType === 'boolean') {
          sample[key] = false;
        } else if (propType === 'array') {
          sample[key] = prop.items ? (() => { try { const s = generateSampleBody(prop.items); return s ? [JSON.parse(s)] : []; } catch { return []; } })() : [];
        } else if (propType === 'object') {
          const nested = generateSampleBody(prop);
          sample[key] = nested ? (() => { try { return JSON.parse(nested); } catch { return {}; } })() : {};
        } else {
          sample[key] = 'string';
        }
      });
      const cleaned = stripExcludedServerSetFields(sample);
      return JSON.stringify(cleaned, null, 2);
    } else if (schema.type === 'array' && schema.items) {
      const sampleItem = generateSampleBody(schema.items);
      return JSON.stringify([JSON.parse(sampleItem)], null, 2);
    } else if (schema.type === 'string') {
      return '"string"';
    } else if (schema.type === 'number' || schema.type === 'integer') {
      return '0';
    } else if (schema.type === 'boolean') {
      return 'false';
    }
  } catch (error) {
    console.error('Error generating sample body:', error);
  }
  
  return '';
}

// Helper to extract request body content
function extractRequestBodyContent(op: any, spec?: any): { body: string, contentType: string } {
  if (op.requestBody && op.requestBody.content) {
    const content = op.requestBody.content as any;
    const contentTypes = Object.keys(content);
    
    // Prefer application/json
    if (content['application/json']) {
      const jsonContent = content['application/json'];
      const schema = jsonContent.schema;
      if (schema) {
        const resolvedSchema = schema['$ref'] && spec ? resolveRef(schema['$ref'], spec) : schema;
        const sampleBody = generateSampleBody(resolvedSchema);
        return { body: sampleBody, contentType: 'JSON' };
      }
    }
    
    // Check other content types
    for (const contentType of contentTypes) {
      const contentData = content[contentType];
      if (contentData && contentData.schema) {
        const schema = contentData.schema;
        const resolvedSchema = schema['$ref'] && spec ? resolveRef(schema['$ref'], spec) : schema;
        
        if (contentType === 'text/plain') {
          return { body: 'Sample text content', contentType: 'Text' };
        } else if (contentType === 'application/javascript') {
          return { body: '// Sample JavaScript code\nconsole.log("Hello World");', contentType: 'JavaScript' };
        } else if (contentType === 'text/html') {
          return { body: '<html><body><h1>Sample HTML</h1></body></html>', contentType: 'HTML' };
        } else if (contentType === 'application/xml' || contentType === 'text/xml') {
          return { body: '<?xml version="1.0"?>\n<root>\n  <element>Sample XML</element>\n</root>', contentType: 'XML' };
        } else {
          // For other types, try to generate sample from schema
          const sampleBody = generateSampleBody(resolvedSchema);
          return { body: sampleBody, contentType: 'JSON' };
        }
      }
    }
  }
  
  return { body: '', contentType: 'JSON' };
}

// Helper to extract schema from requestBody
function extractRequestBodySchema(op: any, spec?: any) {
  if (op.requestBody && op.requestBody.content) {
    const content = op.requestBody.content as any;
    // Prefer application/json
    if (content['application/json'] && (content['application/json'] as any).schema) {
      const schema = (content['application/json'] as any).schema;
      if (schema['$ref'] && spec) {
        return resolveRef(schema['$ref'], spec);
      }
      return schema;
    }
    // Fallback to first available
    const first = Object.values(content)[0] as any;
    if (first && first.schema) {
      if (first.schema['$ref'] && spec) {
        return resolveRef(first.schema['$ref'], spec);
      }
      return first.schema;
    }
  }
  return null;
}

// Helper to extract response schemas
function extractResponseSchemas(op: any, spec?: any) {
  const schemas: Record<string, any> = {};
  if (op.responses) {
    Object.entries(op.responses).forEach(([status, resp]: any) => {
      if (resp.content) {
        const respContent = resp.content as any;
        // Prefer application/json
        if (respContent['application/json'] && (respContent['application/json'] as any).schema) {
          const schema = (respContent['application/json'] as any).schema;
          if (schema['$ref'] && spec) {
            schemas[status] = resolveRef(schema['$ref'], spec);
          } else {
            schemas[status] = schema;
          }
        } else {
          // Fallback to first available
          const first = Object.values(respContent)[0] as any;
          if (first && first.schema) {
            if (first.schema['$ref'] && spec) {
              schemas[status] = resolveRef(first.schema['$ref'], spec);
            } else {
              schemas[status] = first.schema;
            }
          }
        }
      }
    });
  }
  return Object.keys(schemas).length > 0 ? schemas : null;
}

// Parse OpenAPI spec from URL (enhanced version)
export async function parseOpenApiSpec(url: string) {
  // Use proxy service for all requests, including Swagger imports
  const { proxyService } = await import('../../services/proxyService');
  
  try {
    const response = await proxyService.sendRequest({
      url: url,
      method: 'GET',
      timeout: 30000
    });
    
    if (response.status !== 200) {
      throw new Error(`Failed to fetch OpenAPI spec: ${response.status} ${response.statusText}`);
    }
    
        const spec = typeof response.data === 'string' ? JSON.parse(response.data) : response.data;
    
    // Basic parsing: group by tags, extract endpoints
    const collections: any[] = [];
    const tagMap: Record<string, any> = {};
    Object.entries(spec.paths || {}).forEach(([path, methods]: any) => {
      Object.entries(methods).forEach(([method, op]: any) => {
        const tags = op.tags || ['Default'];
        tags.forEach((tag: string) => {
          if (!tagMap[tag]) tagMap[tag] = { name: tag, requests: [] };
          
          // Extract body content and content type
          const { body, contentType } = extractRequestBodyContent(op, spec);
          
          tagMap[tag].requests.push({
            name: op.summary || `${method.toUpperCase()} ${path}`,
            method: method.toUpperCase(),
            path: path,
            url: path,
            duplicate: false,
            headers: op.headers || null,
            body: body,
            bodyType: body ? 'raw' : 'none',
            rawBodySubtype: contentType,
            parameters: op.parameters || null,
            requestBodySchema: extractRequestBodySchema(op, spec),
            responseSchema: extractResponseSchemas(op, spec)
          });
        });
      });
    });
    Object.values(tagMap).forEach(col => collections.push(col));
    // Auth detection (basic)
    const auth = spec.components?.securitySchemes || null;
    return { collections, auth };
    
  } catch (error: any) {
    console.error('Error fetching OpenAPI spec:', error);
    throw new Error(`Failed to fetch OpenAPI spec: ${error.message || error}`);
  }
}

// Detect duplicates by comparing with existing collections/requests
export function detectDuplicates(parsed: any, existingCollections: any[]) {
  console.log('Existing collections:', existingCollections);
  
  // Create maps for faster lookup
  const existingCollectionNames = new Set();
  const existingRequestMap = new Map(); // collectionName -> Set of request identifiers
  
  existingCollections.forEach((col: any) => {
    existingCollectionNames.add(col.name);
    const requestIdentifiers = new Set();
    (col.requests || []).forEach((req: any) => {
      // Create a unique identifier for each request: method + url + name
      const identifier = `${req.method}_${req.url}_${req.name}`;
      requestIdentifiers.add(identifier);
      console.log(`Existing request identifier: ${identifier}`);
    });
    existingRequestMap.set(col.name, requestIdentifiers);
  });

  console.log('Existing request map:', existingRequestMap);

  parsed.collections.forEach((col: any) => {
    // Check if collection already exists
    col.duplicate = existingCollectionNames.has(col.name);
    console.log(`Collection ${col.name} duplicate: ${col.duplicate}`);
    
    col.requests.forEach((req: any) => {
      // Create a unique identifier for this request
      const requestIdentifier = `${req.method}_${req.url}_${req.name}`;
      
      // Check if request already exists in the same collection
      const existingRequests = existingRequestMap.get(col.name);
      req.duplicate = existingRequests ? existingRequests.has(requestIdentifier) : false;
      
      console.log(`New request identifier: ${requestIdentifier}, duplicate: ${req.duplicate}`);
    });
  });
  
  return parsed;
}

// Generate import report (simulate import)
export function generateImportReport(selections: any, existingCollections: any[]) {
  const success: string[] = [];
  const skipped: string[] = [];
  const failed: string[] = [];
  
  let collectionsCreated = 0;
  let requestsCreated = 0;
  let collectionsSkipped = 0;
  let requestsSkipped = 0;
  
  selections.forEach((col: any) => {
    if (!col.selected) return;
    
    if (col.duplicate) {
      collectionsSkipped++;
      skipped.push(`Collection: ${col.name} (already exists)`);
    } else {
      collectionsCreated++;
      success.push(`Collection: ${col.name}`);
    }
    
    col.requests.forEach((req: any) => {
      if (req.selected) {
        if (req.duplicate) {
          requestsSkipped++;
          skipped.push(`${col.name}: ${req.method} ${req.path} (already exists)`);
        } else {
          requestsCreated++;
          success.push(`${col.name}: ${req.method} ${req.path}`);
        }
      }
    });
  });
  
  return { 
    success, 
    skipped, 
    failed,
    summary: {
      collectionsCreated,
      requestsCreated,
      collectionsSkipped,
      requestsSkipped
    }
  };
}

/** Keeps only selected collections and requests; strips UI-only `selected` flags for the import API payload. */
export function filterSwaggerSelectionsForImport(selections: any[] | null | undefined): any[] {
  if (!Array.isArray(selections)) return [];
  const out: any[] = [];
  for (const col of selections) {
    if (!col || col.selected !== true) continue;
    const reqs = Array.isArray(col.requests)
      ? col.requests.filter((r: any) => r && r.selected === true)
      : [];
    if (reqs.length === 0) continue;
    const { selected: _cSel, requests: _reqs, ...colRest } = col;
    out.push({
      ...colRest,
      requests: reqs.map((r: any) => {
        const { selected: _rSel, path, ...reqRest } = r;
        const url = reqRest.url ?? path ?? '';
        let headers = reqRest.headers;
        if (headers != null && typeof headers === 'object') {
          headers = JSON.stringify(headers);
        }
        return { ...reqRest, url, headers };
      }),
    });
  }
  return out;
}
